import type {
  FrameApi,
  FrameData,
  LabelingApi,
  ObjectClass,
  Project,
  ProjectApi,
  StatisticApi,
  TaskApi,
  TrackApi,
} from '../../type';

export const PROJECT_KEYS = {
  getDetail: (id?: Project['id']) =>
    id
      ? (['projectApi.getDetail', id] as const)
      : (['projectApi.getDetail'] as const),
  getList: (filter?: Partial<ProjectApi.GetList.Request>) =>
    filter
      ? (['projectApi.getList', filter] as const)
      : (['projectApi.getList'] as const),
  getCount: (filter?: Partial<ProjectApi.GetList.Request>) =>
    filter
      ? (['projectApi.getCount', filter] as const)
      : (['projectApi.getCount'] as const),
};

export const TASK_KEYS = {
  getDetail: (filter?: Partial<TaskApi.GetDetail.Request>) =>
    filter
      ? (['taskApi.getDetail', filter] as const)
      : (['taskApi.getDetail'] as const),
  getList: (filter?: Partial<TaskApi.GetList.Request>) =>
    filter
      ? (['taskApi.getList', filter] as const)
      : (['taskApi.getList'] as const),
  getCount: (filter?: TaskApi.GetCount.Request) =>
    filter
      ? (['taskApi.getCount', filter] as const)
      : (['taskApi.getCount'] as const),
  getBoxSchema: () => ['taskApi.getBoxSchema'] as const,
};

export const FRAME_KEYS = {
  getDetail: (param?: {
    taskID: FrameData['taskID'];
    frameSeq: FrameData['frameSeq'];
  }) => {
    return param
      ? ['frameApi.getDetail', param]
      : (['frameApi.getDetail'] as const);
  },
  getList: (filter?: Partial<FrameApi.GetList.Request>) =>
    filter
      ? (['frameApi.getList', filter] as const)
      : (['frameApi.getList'] as const),
  getCount: (
    param?: Partial<{
      workerID: string[] | null;
      reviewerID: string[] | null;
    }> & {
      workState?: FrameData['workState'][];
    } & Pick<FrameData, 'taskID'>,
  ) =>
    param
      ? (['frameApi.getCount', param] as const)
      : (['frameApi.getCount'] as const),
  getNeighbor: (param?: FrameApi.GetNeighbor.Request) =>
    param
      ? (['frameApi.getNeighbor', param] as const)
      : (['frameApi.getNeighbor'] as const),
  getHistoryList: (param?: FrameApi.GetHistoryList.Request) =>
    param
      ? (['frameApi.getHistoryList', param] as const)
      : (['frameApi.getHistoryList'] as const),
  /**
   * 이력 패널의 무한 조회 전용 키입니다. limit 기본값 상향으로
   * `useFrameHistoryListQuery` 단건 조회와 파라미터가 같아질 수 있어,
   * 'infinite' 구분자를 덧붙여 캐시 데이터 모양 충돌을 피합니다. 접두사는
   * 그대로 `frameApi.getHistoryList` 이므로 기존 무효화 호출과 계속
   * 매칭됩니다.
   */
  getInfiniteHistoryList: (param: FrameApi.GetHistoryList.Request) =>
    ['frameApi.getHistoryList', param, 'infinite'] as const,
  getAllocatableFrameList: (
    param?: FrameApi.GetAllocatableFrameList.Request,
  ) =>
    param
      ? (['frameApi.getAllocatableFrameList', param] as const)
      : (['frameApi.getAllocatableFrameList'] as const),
};

export const STATISTIC_KEYS = {
  getObject: (params?: StatisticApi.GetObject.Request) =>
    params
      ? (['statisticApi.getObject', JSON.stringify(params)] as const)
      : (['statisticApi.getObject'] as const),
  getState: (params?: StatisticApi.GetState.Request) =>
    params
      ? (['statisticApi.getState', JSON.stringify(params)] as const)
      : (['statisticApi.getState'] as const),
  getMetric: (params?: StatisticApi.GetMetric.Request) =>
    params
      ? (['statisticApi.getMetric', JSON.stringify(params)] as const)
      : (['statisticApi.getMetric'] as const),
  getWorktime: (params?: StatisticApi.GetWorktimeForAdmin.Request) =>
    params
      ? (['statisticApi.getWorktime', JSON.stringify(params)] as const)
      : (['statisticApi.getWorktime'] as const),
};

export const LABELLING_KEYS = {
  getObjectClassList: (param?: LabelingApi.GetObjectClassList.Request) =>
    param
      ? (['labelingApi.getObjectClassList', JSON.stringify(param)] as const)
      : (['labelingApi.getObjectClassList'] as const),
  getObjectClass: (pObjectClassID: ObjectClass['id']) =>
    ['labelingApi.getObjectClass', pObjectClassID] as const,
  createObjectClass: (params: LabelingApi.CreateObjectClass.Request) =>
    ['labelingApi.createObjectClass', JSON.stringify(params)] as const,
  updateObjectClass: (pObjectClassID: ObjectClass['id']) =>
    ['labelingApi.updateObjectClass', pObjectClassID] as const,
};

export const ICON_KEYS = {
  getObjectClassIconList: (
    params?: LabelingApi.GetExtendClassIconList.Request,
  ) =>
    params
      ? (['iconApi.getObjectClassIconList', JSON.stringify(params)] as const)
      : (['iconApi.getObjectClassIconList'] as const),
};

export const TRACK_KEYS = {
  getTrackByFrameSeq: (param?: TrackApi.GetTrackByFrameSeq.Request) =>
    param
      ? (['trackApi.getTrackByFrameSeq', JSON.stringify(param)] as const)
      : (['trackApi.getTrackByFrameSeq'] as const),
};
